/**
 * Utilidades para trabajar con colores
 */

/**
 * Convierte un color hexadecimal a RGB
 * @param hex Color en formato #RRGGBB o #RGB
 * @returns Objeto con componentes r, g, b (0-255)
 */
export function hexToRgb( hex: string ): { r: number; g: number; b: number } | null {
  // Eliminar el # si existe
  hex = hex.replace( /^#/, '' );

  // Expandir formato corto (#RGB -> #RRGGBB)
  if( hex.length === 3 ) {
    hex = hex.split( '' ).map( char => char + char ).join( '' );
  }

  if( hex.length !== 6 ) {
    return null;
  }

  const r = parseInt( hex.substring( 0, 2 ), 16 );
  const g = parseInt( hex.substring( 2, 4 ), 16 );
  const b = parseInt( hex.substring( 4, 6 ), 16 );

  return { r, g, b };
}

/**
 * Calcula la luminosidad relativa de un color según WCAG 2.0
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 *
 * @param r Componente rojo (0-255)
 * @param g Componente verde (0-255)
 * @param b Componente azul (0-255)
 * @returns Luminosidad relativa (0-1)
 */
export function getRelativeLuminance( r: number, g: number, b: number ): number {
  // Convertir a valores 0-1
  const [rs, gs, bs] = [r / 255, g / 255, b / 255];

  // Aplicar función gamma
  const toLinear = ( c: number ) => c <= 0.03928 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 );

  const rLinear = toLinear( rs );
  const gLinear = toLinear( gs );
  const bLinear = toLinear( bs );

  // Calcular luminosidad según coeficientes WCAG
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Calcula el ratio de contraste entre dos colores según WCAG 2.0
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 *
 * @param l1 Luminosidad del primer color (0-1)
 * @param l2 Luminosidad del segundo color (0-1)
 * @returns Ratio de contraste (1-21)
 */
export function getContrastRatio( l1: number, l2: number ): number {
  const lighter = Math.max( l1, l2 );
  const darker = Math.min( l1, l2 );
  return ( lighter + 0.05 ) / ( darker + 0.05 );
}

/**
 * Determina si se debe usar texto blanco o negro sobre un color de fondo
 * para cumplir con los estándares WCAG AA (ratio >= 4.5:1 para texto normal)
 *
 * @param backgroundColor Color de fondo en formato hexadecimal (#RRGGBB)
 * @returns Color de texto recomendado ('#FFFFFF' o '#000000')
 */
export function getContrastingTextColor( backgroundColor: string ): string {
  const rgb = hexToRgb( backgroundColor );

  if( !rgb ) {
    // Fallback si el color no es válido
    return '#FFFFFF';
  }

  const bgLuminance = getRelativeLuminance( rgb.r, rgb.g, rgb.b );

  // Luminosidad del blanco y negro
  const whiteLuminance = 1;
  const blackLuminance = 0;

  // Calcular contraste con blanco y negro
  const contrastWithWhite = getContrastRatio( bgLuminance, whiteLuminance );
  const contrastWithBlack = getContrastRatio( bgLuminance, blackLuminance );

  // Retornar el color con mejor contraste
  // Favorecemos blanco si el contraste es similar (diferencia < 1.5)
  if( contrastWithWhite > contrastWithBlack ) {
    return '#FFFFFF';
  } else if( contrastWithBlack > contrastWithWhite + 1.5 ) {
    return '#000000';
  } else {
    // Si los contrastes son similares, usar blanco por estética
    return '#FFFFFF';
  }
}

/**
 * Determina si un color es claro u oscuro
 * Útil para decisiones simples sin cálculo completo de contraste
 *
 * @param backgroundColor Color en formato hexadecimal
 * @returns true si el color es claro, false si es oscuro
 */
export function isLightColor( backgroundColor: string ): boolean {
  const rgb = hexToRgb( backgroundColor );

  if( !rgb ) {
    return false;
  }

  const luminance = getRelativeLuminance( rgb.r, rgb.g, rgb.b );

  // Threshold en 0.5 (mitad del rango 0-1)
  return luminance > 0.5;
}
